import { userApi } from "../../../redux/api/axiosInstance";
import { registerSessionCache, sessionGeneration } from '../../../utils/sessionCaches';

// Base Permission interfaces
export interface Permission {
  permissionId: number;
  permissionName: string;
  description: string;
}

export interface PermissionResponse {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  data: Permission[];
}

export interface CreatePermissionPayload {
  permissionId: number;
  permissionName: string;
  description: string;
}

export interface UpdatePermissionPayload {
  permissionId: number;
  permissionName: string;
  description: string;
}

// Permission Set interfaces
export interface PermissionSet {
  permissionSetId: number;
  name: string;
  description?: string;
  permissions?: Permission[];
}

export interface PermissionSetResponse {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  data: PermissionSet[];
}

export interface CreatePermissionSetPayload {
  name: string;
  description?: string;
}

export interface UpdatePermissionSetPayload {
  permissionSetId: number;
  name: string;
  description?: string;
}

// Assignment interfaces
export interface AssignPermissionToUserPayload {
  userId: number;
  permissionId: number;
}

export interface AssignPermissionToRolePayload {
  roleId: number;
  permissionId: number;
}

export interface AssignPermissionSetToUserPayload {
  userId: number;
  permissionSetId: number;
}

export interface AssignPermissionSetToRolePayload {
  roleId: number;
  permissionSetId: number;
}

export interface EffectivePermissions {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  data: Permission[];
}

// ============= PERMISSION CRUD OPERATIONS =============

/**
 * HRMS-90 (P4): the caller's own effective permissions (role + direct union), from
 * GET /api/permissions/me/effective. Used to refresh state.auth.permissions live so
 * permission changes take effect without a re-login, and so the canonical Resource.Action
 * names are present even if the JWT carried only legacy names. Best-effort (returns []).
 */
let effectiveInflight: Promise<string[]> | null = null;
let effectiveCache: { at: number; perms: string[] } | null = null;
const EFFECTIVE_TTL_MS = 5_000;
registerSessionCache(() => { effectiveCache = null; effectiveInflight = null; });

/** Shared in-flight request plus a 5 s cache, so the several callers on one page make one call. */
export const getMyEffectivePermissions = async (force = false): Promise<string[]> => {
  if (!force && effectiveCache && Date.now() - effectiveCache.at < EFFECTIVE_TTL_MS) return effectiveCache.perms;
  if (effectiveInflight) return effectiveInflight;
  const gen = sessionGeneration();
  effectiveInflight = (async () => {
    try {
      const response = await userApi.get('Permissions/me/effective', { quiet403: true });
      // Signed out while this was in flight: the answer belongs to the previous user.
      if (gen !== sessionGeneration()) return [];
      const perms = response.data?.permissions ?? response.data ?? [];
      const list = Array.isArray(perms) ? perms : [];
      effectiveCache = { at: Date.now(), perms: list };
      return list;
    } catch (error) {
      console.warn('Could not refresh effective permissions:', error);
      return [];
    } finally {
      if (gen === sessionGeneration()) effectiveInflight = null;
    }
  })();
  return effectiveInflight;
};

export const getAllPermissions = async (pageNo = 1, pageSize = 10): Promise<PermissionResponse> => {
  try {
    const response = await userApi.get(`Permissions?pageNo=${pageNo}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching permissions:", error);
    throw error;
  }
};

export const getPermissionById = async (permissionId: number): Promise<Permission> => {
  try {
    const response = await userApi.get(`Permissions/details/${permissionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching permission with ID ${permissionId}:`, error);
    throw error;
  }
};

export const createPermission = async (payload: CreatePermissionPayload): Promise<Permission> => {
  try {
    const response = await userApi.post('Permissions/create', payload);
    return response.data;
  } catch (error) {
    console.error("Error creating permission:", error);
    throw error;
  }
};

export const updatePermission = async (payload: UpdatePermissionPayload): Promise<Permission> => {
  try {
    const response = await userApi.put('Permissions/update', payload);
    return response.data;
  } catch (error) {
    console.error("Error updating permission:", error);
    throw error;
  }
};

export const deletePermission = async (permissionId: number): Promise<void> => {
  try {
    await userApi.delete(`/Permissions/delete/${permissionId}`);
  } catch (error) {
    console.error("Error deleting permission:", error);
    throw error;
  }
};

// ============= PERMISSION SETS OPERATIONS =============

export const getAllPermissionSets = async (pageNo = 1, pageSize = 10): Promise<PermissionSetResponse> => {
  try {
    const response = await userApi.get(`Permissions/sets?pageNo=${pageNo}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching permission sets:", error);
    throw error;
  }
};

export const createPermissionSet = async (payload: CreatePermissionSetPayload): Promise<PermissionSet> => {
  try {
    const response = await userApi.post('Permissions/sets/create', payload);
    return response.data;
  } catch (error) {
    console.error("Error creating permission set:", error);
    throw error;
  }
};

export const updatePermissionSet = async (payload: UpdatePermissionSetPayload): Promise<PermissionSet> => {
  try {
    const response = await userApi.put('Permissions/sets/update', payload);
    return response.data;
  } catch (error) {
    console.error("Error updating permission set:", error);
    throw error;
  }
};

export const deletePermissionSet = async (permissionSetId: number): Promise<void> => {
  try {
    await userApi.delete(`/Permissions/sets/delete/${permissionSetId}`);
  } catch (error) {
    console.error("Error deleting permission set:", error);
    throw error;
  }
};

// ============= PERMISSION ASSIGNMENT OPERATIONS =============

export const assignPermissionToUser = async (payload: AssignPermissionToUserPayload): Promise<void> => {
  try {
    await userApi.post('Permissions/assign/user', payload);
  } catch (error) {
    console.error("Error assigning permission to user:", error);
    throw error;
  }
};

// HRMS-110: remove a permission from a role (reverses assignPermissionToRole).
export const removePermissionFromRole = async (payload: AssignPermissionToRolePayload, opts?: { quiet403?: boolean }): Promise<void> => {
  try {
    await userApi.delete('Permissions/remove/role', { data: payload, quiet403: opts?.quiet403 });
  } catch (error) {
    console.error("Error removing permission from role:", error);
    throw error;
  }
};

export const assignPermissionToRole = async (payload: AssignPermissionToRolePayload, opts?: { quiet403?: boolean }): Promise<void> => {
  try {
    await userApi.post('Permissions/assign/role', payload, { quiet403: opts?.quiet403 });
  } catch (error) {
    console.error("Error assigning permission to role:", error);
    throw error;
  }
};

export const assignPermissionSetToUser = async (payload: AssignPermissionSetToUserPayload): Promise<void> => {
  try {
    await userApi.post('Permissions/assign/set/user', payload);
  } catch (error) {
    console.error("Error assigning permission set to user:", error);
    throw error;
  }
};

export const assignPermissionSetToRole = async (payload: AssignPermissionSetToRolePayload, opts?: { quiet403?: boolean }): Promise<void> => {
  try {
    await userApi.post('Permissions/assign/set/role', payload, { quiet403: opts?.quiet403 });
  } catch (error) {
    console.error("Error assigning permission set to role:", error);
    throw error;
  }
};

export const assignPermissionToPermissionSet = async (
  permissionSetId: number, 
  permissionId: number
): Promise<void> => {
  try {
    await userApi.post(`Permissions/assign/set/${permissionSetId}/permission/${permissionId}`);
  } catch (error) {
    console.error("Error assigning permission to permission set:", error);
    throw error;
  }
};

export const getUserEffectivePermissions = async (
  userId: number,
  pageNo = 1,
  pageSize = 1000   // show ALL effective permissions (a role/user can hold 200+); 100 truncated the list
): Promise<EffectivePermissions> => {
  try {
    const response = await userApi.get(`Permissions/user/${userId}/effective-permissions?pageNo=${pageNo}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching effective permissions for user ${userId}:`, error);
    throw error;
  }
};

export const getRoleEffectivePermissions = async (
  roleId: number,
  pageNo = 1,
  pageSize = 1000   // show ALL of the role's permissions (Admin ~250); 100 truncated the list
): Promise<EffectivePermissions> => {
  try {
    const response = await userApi.get(`Permissions/role/${roleId}/effective-permissions?pageNo=${pageNo}&pageSize=${pageSize}`, { quiet403: true });
    return response.data;
  } catch (error) {
    console.error(`Error fetching effective permissions for role ${roleId}:`, error);
    throw error;
  }
};